import {
  addDays, bucketToday, describeRule, newId, nextOccurrences, parseQuickAdd, slugifyTag,
  sortInstances, spanIsNear,
  type CalendarEvent, type IsoDate, type Proposal, type Tag, type Task, type TaskInstance, type User,
} from '@hestia/core';
import { ApiError, Offline, req, surelyOffline } from './net.js';
import { overlayInstances, overlayTags, type Ctx } from './offline/overlay.js';
import { guardOf, stampOf, type Op } from './offline/ops.js';
import { enqueue, pending, readCache, writeCache } from './offline/store.js';
import { drain, noteReachable, refreshWaiting } from './offline/sync.js';

export { ApiError, Offline } from './net.js';

/** The one door between the app and the server.
 *
 *  Every read is network-first and falls back to the last answer we stored; every
 *  write goes straight out when it can and joins a queue when it cannot. Views
 *  above this line do not know which of those happened, which is the point —
 *  there is no "offline mode" to enter, only a connection that comes and goes. */

/** How far back `/api/today` looks for things still open. Must match the server;
 *  it decides which days a locally-created task can surface on. */
const LOOKBACK = 14;

/** Set once the app knows who is signed in. The overlay needs it to attribute a
 *  task created offline and to colour a tag that does not exist on the server yet. */
let ctx: Ctx = { meId: '', tags: [], today: '1970-01-01' };
export function setOverlayContext(next: Partial<Ctx>) {
  ctx = { ...ctx, ...next };
}

/** Network first, cache second. A stale answer beats a blank screen, and the only
 *  thing that ever goes stale here is a list of chores. */
async function read<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
  if (!surelyOffline()) {
    try {
      const fresh = await fetcher();
      void writeCache(key, fresh);
      noteReachable(true);
      return fresh;
    } catch (err) {
      if (!(err instanceof Offline)) throw err;
    }
  }
  noteReachable(false);
  const hit = await readCache<T>(key);
  if (!hit) throw new Offline();
  return hit.value;
}

/** Send it if we can, queue it if we cannot. The caller is told it worked either
 *  way, because either way it will be.
 *
 *  Nothing may jump the queue, even with a perfectly good connection. A task
 *  created in a tunnel does not exist on the server until its create drains, so a
 *  live PATCH against it the moment the signal returns would 404 — and a 404 is
 *  permanent, so that edit would be dropped rather than retried. While anything
 *  is waiting, everything waits, and the order they were made in is the order
 *  they arrive in. */
async function write(op: Op, label: string, live: () => Promise<void>): Promise<void> {
  const waiting = (await pending()).length > 0;
  if (!waiting && !surelyOffline()) {
    try {
      await live();
      noteReachable(true);
      return;
    } catch (err) {
      if (!(err instanceof Offline)) throw err;
      noteReachable(false);
    }
  }
  await enqueue(op, label);
  await refreshWaiting();
  // Behind a queue rather than behind a dead connection: get it moving.
  if (waiting) void drain();
}

const q = (assignee?: string) => assignee ?? 'all';

/** Every task the app has laid eyes on, by id.
 *
 *  A day view carries whole tasks, not just titles, so by the time you tap one
 *  its data is already on the device — it is simply filed under the day rather
 *  than under the task. This keeps a copy filed the other way too, so opening
 *  something offline does not depend on having opened it before. */
async function indexTasks(instances: TaskInstance[]) {
  if (!instances.length) return;
  const known = (await readCache<Record<string, Task>>('tasks'))?.value ?? {};
  let changed = false;
  for (const i of instances) {
    if (known[i.task.id]?.updatedAt !== i.task.updatedAt) { known[i.task.id] = i.task; changed = true; }
  }
  if (changed) await writeCache('tasks', known);
}

export interface TaskList {
  stillOpen: TaskInstance[];
  today: TaskInstance[];
  upcoming: TaskInstance[];
  repeating: TaskInstance[];
  done: TaskInstance[];
}

export interface Interpreted {
  title: string; startDate: IsoDate; startTime: string | null; repeat: string | null;
  assignee: string | null; tags: string[];
  unmatchedAssignee: string | null;
}

export const api = {
  me: () => read('me', () => req<{ user: User | null; today: IsoDate; tz: string }>('GET', '/api/me')),
  logout: () => req<{ ok: true }>('POST', '/api/auth/logout'),

  users: () => read('users', () => req<{ users: User[] }>('GET', '/api/users').then((r) => r.users)),

  tags: async () => {
    const server = await read('tags', () => req<{ tags: Tag[] }>('GET', '/api/tags').then((r) => r.tags));
    return overlayTags(server, await pending());
  },

  /** Every live task, once each, grouped by where it stands. Cached like the
   *  day views, so the list opens offline as it last was; and indexed, so any
   *  task on it can be opened offline too. */
  taskList: async (assignee?: string) => {
    const r = await read(`list:${q(assignee)}`, () =>
      req<TaskList & { date: IsoDate }>(
        'GET', `/api/tasks${assignee ? `?assignee=${encodeURIComponent(assignee)}` : ''}`));
    void indexTasks([...r.stillOpen, ...r.today, ...r.upcoming, ...r.repeating, ...r.done]);
    return r;
  },

  today: async (assignee?: string) => {
    const r = await read(`today:${q(assignee)}`, () =>
      req<{ date: IsoDate; stillOpen: TaskInstance[]; today: TaskInstance[];
            spanning: TaskInstance[]; events: CalendarEvent[] }>(
        'GET', `/api/today${assignee ? `?assignee=${encodeURIComponent(assignee)}` : ''}`));
    const spanning = r.spanning ?? [];
    void indexTasks([...r.stillOpen, ...r.today, ...spanning]);
    const from = addDays(r.date, -LOOKBACK);
    // Spans are laid over the same way, then taken back out of the day buckets:
    // a thing that is on all week is not a thing that is on today.
    const all = overlayInstances([...r.stillOpen, ...r.today, ...spanning], await pending(),
                                 from, addDays(r.date, 7), { ...ctx, today: r.date });
    const near = all.filter((i) => spanIsNear(i, r.date, 7)).sort(sortInstances);
    const nearIds = new Set(near.map((i) => i.task.id));
    const rest = all.filter((i) => !nearIds.has(i.task.id) && i.date <= r.date);
    return { date: r.date, ...bucketToday(rest, r.date), spanning: near, events: r.events };
  },

  range: async (from: IsoDate, to: IsoDate, assignee?: string) => {
    const r = await read(`range:${from}:${to}:${q(assignee)}`, () =>
      req<{ instances: TaskInstance[]; today: IsoDate; events: CalendarEvent[] }>(
        'GET', `/api/instances?from=${from}&to=${to}${assignee ? `&assignee=${encodeURIComponent(assignee)}` : ''}`));
    void indexTasks(r.instances);
    return {
      ...r,
      instances: overlayInstances(r.instances, await pending(), from, to, { ...ctx, today: r.today }),
    };
  },

  task: async (id: string) => {
    const dress = (task: Task) => ({
      task,
      // Both derived from the rule, which travels with the task — no reason to
      // ask the server to describe something the device can read for itself.
      repeatSummary: describeRule(task.rrule),
      next: nextOccurrences(task.startDate, task.rrule, ctx.today, 3),
    });
    /** Edits queued against this task, shown the way every other view shows
     *  them — otherwise opening a task you just renamed offline would show the
     *  old name back. */
    const withPending = async (r: { task: Task; repeatSummary: string; next: IsoDate[] }) => {
      const queue = (await pending()).filter((p) => 'taskId' in p.op && p.op.taskId === id);
      if (!queue.length) return r;
      const seed = { task: r.task, date: r.task.startDate, originalDate: r.task.startDate,
                     endDate: r.task.endDate, done: false, completedBy: null,
                     completedAt: null, stillOpen: false };
      const [after] = overlayInstances([seed], queue, '0000-01-01', '9999-12-31', ctx);
      return after ? dress(after.task) : r;
    };
    // Asked before sending: a task whose create is still queued cannot be on the
    // server, and asking anyway returns a 404 — which is not a missing task, it
    // is a task that has not been posted yet. The connection coming back does not
    // change that, so this check comes before the network, not after it fails.
    const queued = (await pending()).find((p) => p.op.kind === 'create' && p.op.taskId === id);
    if (queued) {
      // Replay the whole queue, not just the create: edits made to this task
      // before it was ever sent are part of what it is. Then pick this task out
      // of the several the queue may have made.
      const made = overlayInstances([], await pending(), '0000-01-01', '9999-12-31', ctx)
        .find((i) => i.task.id === id);
      if (made) return dress(made.task);
    }
    try {
      return await withPending(await read(`task:${id}`, () =>
        req<{ task: Task; repeatSummary: string; next: IsoDate[] }>('GET', `/api/tasks/${id}`)));
    } catch (err) {
      if (!(err instanceof Offline)) throw err;
      // Nothing filed under this id, so look where the app has actually been:
      // the days it has drawn.
      const seen = (await readCache<Record<string, Task>>('tasks'))?.value?.[id];
      if (seen) return dress(seen);
      throw err;
    }
  },

  quickAdd: async (text: string) => {
    if (!surelyOffline()) {
      try {
        return await req<{ task: Task; tags: Tag[]; interpreted: Interpreted }>(
          'POST', '/api/quick-add', { text });
      } catch (err) {
        if (!(err instanceof Offline)) throw err;
      }
    }
    // The same parser the server runs, so a line typed on a train reads exactly
    // the way it would have online — and what it resolved to is what gets queued,
    // rather than the text, which would parse against a different "today" later.
    const draft = parseQuickAdd(text, { today: ctx.today });
    const task = await api.create({
      title: draft.title,
      startDate: draft.startDate,
      startTime: draft.startTime,
      durationMin: draft.durationMin,
      rrule: draft.rrule,
      tags: draft.tagHints,
      assigneeId: ctx.meId,
    });
    return {
      task,
      tags: overlayTags(ctx.tags, await pending()).filter((t) =>
        draft.tagHints.some((h) => slugifyTag(h) === t.slug)),
      interpreted: {
        title: task.title, startDate: task.startDate, startTime: task.startTime,
        repeat: null, assignee: null, tags: draft.tagHints, unmatchedAssignee: null,
      } as Interpreted,
    };
  },

  create: async (input: {
    title: string; startDate: IsoDate; endDate?: IsoDate | null; notes?: string | null;
    startTime?: string | null; durationMin?: number | null;
    assigneeId?: string | null; rrule?: string | null; tags?: string[];
  }): Promise<Task> => {
    // The id is minted here, always — not just when offline. It is what makes a
    // replay land on the same row as the attempt whose answer went missing.
    const body = {
      id: newId(),
      title: input.title,
      notes: input.notes ?? null,
      startDate: input.startDate,
      endDate: input.endDate ?? null,
      startTime: input.startTime ?? null,
      durationMin: input.durationMin ?? null,
      assigneeId: input.assigneeId ?? null,
      rrule: input.rrule ?? null,
      tags: input.tags ?? [],
    };
    let created: Task | null = null;
    await write({ kind: 'create', taskId: body.id, input: body }, input.title, async () => {
      created = (await req<{ task: Task }>('POST', '/api/tasks', body)).task;
    });
    if (created) return created;
    const local = overlayInstances([], await pending(), body.startDate, body.startDate, ctx)
      .find((i) => i.task.id === body.id);
    return local?.task ?? ({ ...body, tagIds: [], createdBy: ctx.meId, remindMinBefore: null,
                             createdAt: '', updatedAt: '' } as unknown as Task);
  },

  update: async (
    id: string,
    patch: Omit<Partial<Task>, 'tagIds'> & { tags?: string[] },
    base?: number,
  ): Promise<Task> => {
    let updated: Task | null = null;
    await write(
      { kind: 'update', taskId: id, patch: patch as Record<string, unknown>, baseUpdatedAt: base ?? 0 },
      patch.title ?? id,
      async () => { updated = (await req<{ task: Task }>('PATCH', `/api/tasks/${id}`, patch)).task; },
    );
    if (updated) return updated;
    // Queued rather than sent, so there is no server answer to hand back. The
    // truth about this task is now whatever the app knows plus the queue
    // replayed over it — including the edit just added — which is exactly what
    // `task()` builds. Merging the patch into a cached row instead breaks the
    // moment there is no cached row, as there is not for a task created on this
    // device: the caller gets an object with a title and nothing else.
    return (await api.task(id)).task;
  },

  remove: (id: string, label = id) =>
    write({ kind: 'delete', taskId: id }, label, () => req<void>('DELETE', `/api/tasks/${id}`)),

  complete: (i: { task: { id: string; title: string }; date: IsoDate; originalDate: IsoDate; done: boolean },
             done: boolean) =>
    // Exceptions are keyed by the day the *schedule* produced, not the day the
    // task ended up on. Passing `date` here would write a second exception at the
    // new date and leave the move behind, so a task moved to today could not be
    // ticked off.
    write(
      { kind: 'complete', taskId: i.task.id, date: i.originalDate, done, baseStatus: guardOf(i) },
      i.task.title,
      () => req<{ ok: true }>('POST', `/api/tasks/${i.task.id}/complete`, { date: i.originalDate, done })
        .then(() => undefined),
    ),

  move: (i: { task: { id: string; title: string }; date: IsoDate; originalDate: IsoDate; done: boolean },
         to: IsoDate) =>
    write(
      { kind: 'move', taskId: i.task.id, date: i.originalDate, to, baseStatus: guardOf(i) },
      i.task.title,
      () => req<{ ok: true }>('POST', `/api/tasks/${i.task.id}/move`, { date: i.originalDate, to })
        .then(() => undefined),
    ),

  // The calendar half talks to Google through the server, so there is nothing
  // sensible to queue: these need a connection and say so when they do not have one.
  /** Whether the household can talk to the app at all — false when the machine
   *  has no transcriber, in which case the button is never offered. */
  voiceAvailable: () => req<{ available: boolean }>('GET', '/api/voice').then((r) => r.available),

  /** A recording, in; what was understood, back. Nothing is saved by this — the
   *  answer is a set of proposals the person still has to approve. */
  speak: async (audio: Blob) => {
    const form = new FormData();
    const ext = audio.type.includes('webm') ? 'webm' : 'm4a';
    form.append('audio', audio, `note.${ext}`);
    const res = await fetch('/api/voice', { method: 'POST', credentials: 'same-origin', body: form });
    const body = await res.json().catch(() => null) as
      { transcript?: string; proposals?: Proposal[]; error?: string } | null;
    if (!res.ok) throw new ApiError(res.status, body?.error ?? `http_${res.status}`);
    return { transcript: body?.transcript ?? '', proposals: body?.proposals ?? [] };
  },

  calendar: () => read('calendar', () => req<CalendarStatus>('GET', '/api/calendar')),
  calendarRefresh: () => req<{ added: number }>('POST', '/api/calendar/refresh'),
  calendarToggle: (id: string, enabled: boolean) =>
    req<{ ok: true }>('PATCH', `/api/calendar/${encodeURIComponent(id)}`, { enabled }),
  calendarSync: () => req<{ calendars: number; events: number } & CalendarStatus>('POST', '/api/calendar/sync'),
  calendarDisconnect: () => req<void>('DELETE', '/api/calendar'),
};

export { stampOf, drain };

export interface CalendarStatus {
  connected: boolean;
  lastSyncAt: number | null;
  lastError: string | null;
  calendars: Array<{ id: string; summary: string; hue: string; enabled: boolean }>;
}
